/**
 * WebSocket context provider for sharing real-time price updates across the app.
 */

import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from 'react'

interface PriceUpdate {
  ticker: string
  asset_id: number
  price: string
  change?: string
  change_percent?: string
  currency: string
  source: string
  type: string
}

interface WebSocketContextValue {
  isConnected: boolean
  isConnecting: boolean
  lastPriceUpdate: PriceUpdate[] | null
  lastUpdateTime: Date | null
  priceMap: Map<string, PriceUpdate>
  error: string | null
  connect: () => void
  disconnect: () => void
  subscribe: (portfolioId: number) => void
  unsubscribe: (portfolioId: number) => void
  getPrice: (ticker: string) => PriceUpdate | undefined
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

const WS_BASE_URL = import.meta.env.VITE_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`

interface WebSocketProviderProps {
  children: React.ReactNode
  autoConnect?: boolean
}

export function WebSocketProvider({ children, autoConnect = true }: WebSocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [lastPriceUpdate, setLastPriceUpdate] = useState<PriceUpdate[] | null>(null)
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null)
  const [priceMap, setPriceMap] = useState<Map<string, PriceUpdate>>(new Map())
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttempts = useRef(0)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const subscribedPortfolios = useRef<Set<number>>(new Set())

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
  }, [])

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data)

      switch (message.type) {
        case 'price_update': {
          const prices = message.data.prices as PriceUpdate[]
          setLastPriceUpdate(prices)
          setLastUpdateTime(new Date(message.timestamp))

          // Update price map
          setPriceMap((prev) => {
            const newMap = new Map(prev)
            prices.forEach((p) => {
              newMap.set(p.ticker, p)
            })
            return newMap
          })
          break
        }

        case 'connected':
          console.log('[WebSocket] Connected to server')
          // Re-subscribe to previously subscribed portfolios
          subscribedPortfolios.current.forEach((portfolioId) => {
            wsRef.current?.send(JSON.stringify({
              action: 'subscribe',
              portfolio_id: portfolioId,
            }))
          })
          break

        case 'error':
          console.error('[WebSocket] Server error:', message.message)
          setError(message.message || 'Unknown error')
          break
      }
    } catch (err) {
      console.error('[WebSocket] Failed to parse message:', err)
    }
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    const token = localStorage.getItem('access_token')
    if (!token) {
      setError('No authentication token available')
      return
    }

    setIsConnecting(true)
    setError(null)

    try {
      const wsUrl = `${WS_BASE_URL}/api/v1/ws/prices?token=${encodeURIComponent(token)}`
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        console.log('[WebSocket] Connection established')
        setIsConnected(true)
        setIsConnecting(false)
        setError(null)
        reconnectAttempts.current = 0

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }))
          }
        }, 30000)
      }

      ws.onmessage = handleMessage

      ws.onerror = () => {
        setError('WebSocket connection error')
      }

      ws.onclose = (event) => {
        console.log(`[WebSocket] Connection closed: ${event.code}`)
        setIsConnected(false)
        setIsConnecting(false)
        clearTimers()

        // Attempt reconnect
        if (event.code !== 1000 && event.code !== 4001 && reconnectAttempts.current < 5) {
          reconnectAttempts.current++
          const delay = 3000 * Math.pow(2, reconnectAttempts.current - 1)
          reconnectTimeoutRef.current = setTimeout(connect, delay)
        }
      }

      wsRef.current = ws
    } catch (err) {
      console.error('[WebSocket] Failed to connect:', err)
      setError('Failed to establish WebSocket connection')
      setIsConnecting(false)
    }
  }, [handleMessage, clearTimers])

  const disconnect = useCallback(() => {
    clearTimers()
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected')
      wsRef.current = null
    }
    setIsConnected(false)
    subscribedPortfolios.current.clear()
  }, [clearTimers])

  const subscribe = useCallback((portfolioId: number) => {
    subscribedPortfolios.current.add(portfolioId)
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'subscribe',
        portfolio_id: portfolioId,
      }))
    }
  }, [])

  const unsubscribe = useCallback((portfolioId: number) => {
    subscribedPortfolios.current.delete(portfolioId)
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'unsubscribe',
        portfolio_id: portfolioId,
      }))
    }
  }, [])

  const getPrice = useCallback((ticker: string) => {
    return priceMap.get(ticker)
  }, [priceMap])

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      // Delay connection to ensure auth token is available
      const timer = setTimeout(connect, 1000)
      return () => {
        clearTimeout(timer)
        disconnect()
      }
    }
    return () => disconnect()
  }, [autoConnect, connect, disconnect])

  const value: WebSocketContextValue = {
    isConnected,
    isConnecting,
    lastPriceUpdate,
    lastUpdateTime,
    priceMap,
    error,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    getPrice,
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider')
  }
  return context
}

export default WebSocketContext
